import { Button } from "@/components/ui/button";
import type { HtmlBlockPaneContext } from "@/panes/file-record-detail/useOpenFileRecord";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { formatFileSize } from "../contentType";

export type HtmlPaneView = "preview" | "source" | "info";

/**
 * Slim segmented control under the pane header switching the HTML record's
 * backstage views. The pane deliberately stops competing with the canvas at
 * rendering the block; Preview shows it at its authored size, and Source /
 * Info carry what the canvas card can't show.
 */
export function HtmlViewBar({
  view,
  onViewChange,
  right,
}: {
  view: HtmlPaneView;
  onViewChange: (view: HtmlPaneView) => void;
  right?: React.ReactNode;
}) {
  const tabs: Array<{ key: HtmlPaneView; label: string }> = [
    { key: "preview", label: "Preview" },
    { key: "source", label: "Source" },
    { key: "info", label: "Info" },
  ];
  return (
    <div className="flex h-9 shrink-0 items-center gap-1 border-b bg-background/60 px-2">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          aria-pressed={view === tab.key}
          onClick={() => onViewChange(tab.key)}
          className={cn(
            "rounded-md px-2 py-0.5 text-xs transition-colors",
            view === tab.key
              ? "bg-muted font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {tab.label}
        </button>
      ))}
      <div className="ml-auto flex items-center gap-2">{right}</div>
    </div>
  );
}

/** Read-only raw HTML with a copy button. `body` is the pre-presign source of
 *  truth — the same bytes exports use. */
export function HtmlSourceView({ body }: { body: string | null }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  if (body == null) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Source not loaded.
      </div>
    );
  }
  return (
    <div className="relative h-full">
      <div className="absolute right-3 top-2 z-10">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => {
            void navigator.clipboard?.writeText(body).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <div className="h-full overflow-auto p-4">
        <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-foreground/90">
          {body}
        </pre>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-3 py-2">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right text-xs text-foreground">
        {children}
      </span>
    </div>
  );
}

function FlagChip({ label, on }: { label: string; on: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px]",
        on
          ? "border-transparent bg-muted font-medium text-foreground"
          : "text-muted-foreground/70",
      )}
    >
      {label}
    </span>
  );
}

/**
 * Record + placement metadata — the "File Details" idiom from
 * FallbackContent, extended with the block's canvas footprint and display
 * flags when the pane was opened from a canvas node.
 */
export function HtmlInfoView({
  record,
  blockContext,
}: {
  record: FileRecord;
  blockContext?: HtmlBlockPaneContext;
}) {
  const presentation = blockContext?.presentation;
  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
  };
  return (
    <div className="h-full overflow-auto p-4">
      <div className="mx-auto flex max-w-md flex-col gap-4">
        <div className="divide-y rounded-lg border bg-muted/20">
          {blockContext && (
            <InfoRow label="Canvas size">
              {blockContext.width} × {blockContext.height}
            </InfoRow>
          )}
          {presentation && (
            <InfoRow label="Display">
              <span className="flex flex-wrap justify-end gap-1">
                <FlagChip
                  label="Interactive"
                  on={presentation.interactive === true}
                />
                <FlagChip
                  label="Frameless"
                  on={presentation.frameless === true}
                />
                <FlagChip
                  label="Transparent"
                  on={presentation.transparent === true}
                />
                {presentation.layer === "back" && (
                  <FlagChip label="Back layer" on />
                )}
              </span>
            </InfoRow>
          )}
          <InfoRow label="Type">{record.content_type}</InfoRow>
          <InfoRow label="Size">{formatFileSize(record.size)}</InfoRow>
          <InfoRow label="Created">{formatDate(record.created_at)}</InfoRow>
          <InfoRow label="Updated">{formatDate(record.updated_at)}</InfoRow>
        </div>
        {record.generation_prompt && (
          <div className="rounded-lg border bg-muted/20 p-3">
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              Generation prompt
            </div>
            <div className="max-h-48 overflow-auto whitespace-pre-wrap text-xs leading-relaxed text-foreground/90">
              {record.generation_prompt}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
